import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadCloud, X, CheckCircle2 } from "lucide-react";

export default function SKUUploadDialog({ open, onOpenChange, onCommitted }) {
  const { current } = useWorkspace();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setResult(null);
  }, [open]);

  const onFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    if (list[0]) setFile(list[0]);
  };

  const upload = async () => {
    if (!current || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/skus/upload", fd, {
        params: { workspace_id: current.id },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.total_processed} SKU(s) · ${data.inserted} new · ${data.updated} updated`);
      onCommitted?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-xl" data-testid="sku-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Upload SKU Database</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Required headers: <span className="font-mono">SKU, Product, Category, MRP, COGS, Packaging, GST, Unit Cost</span>. Rows are upserted using SKU as the unique key.
          </p>
        </DialogHeader>

        {!result && (
          <div className="space-y-5">
            <div>
              <Label className="overline">CSV File</Label>
              <label
                htmlFor="sku-upload-input"
                className="mt-2 border-2 border-dashed border-border p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-accent transition-colors"
                data-testid="sku-upload-dropzone"
              >
                <UploadCloud className="h-8 w-8 text-muted-foreground mb-2" />
                <div className="text-sm">Click to select a SKU database CSV</div>
                <div className="text-xs text-muted-foreground mt-1">Existing SKUs in this workspace are overwritten in place.</div>
                <input
                  id="sku-upload-input"
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={onFileChange}
                  data-testid="sku-upload-file-input"
                />
              </label>
              {file && (
                <div className="mt-3 flex items-center justify-between border border-border px-3 py-2" data-testid="sku-upload-filename">
                  <div className="text-sm font-mono truncate">{file.name}</div>
                  <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
              <Button
                onClick={upload}
                disabled={uploading || !file}
                className="rounded-none"
                data-testid="sku-upload-submit"
              >
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-5">
            <div className="border border-border p-4 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[hsl(var(--positive))] shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium">{result.total_processed} SKU(s) processed</div>
                <div className="mt-1 text-muted-foreground font-mono text-xs">
                  {result.inserted} new · {result.updated} updated{result.skipped ? ` · ${result.skipped} skipped` : ""}
                </div>
                {result.skipped_samples?.length > 0 && (
                  <div className="mt-2 text-[10px] text-[hsl(var(--negative))]">
                    First skipped: {result.skipped_samples.map((s, i) => (
                      <span key={i} className="block font-mono">{s.reason}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setResult(null); setFile(null); }} className="rounded-none">Upload another</Button>
              <Button onClick={() => onOpenChange(false)} className="rounded-none" data-testid="sku-upload-done">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
